/** Usuario de sesión (login / refresh). `isAdmin` llega camelCase desde `is_admin` del API. */
export type NotasUser = {
  rol?: string | { nombre?: string } | null;
  isAdmin?: boolean;
  is_admin?: boolean;
  isStaff?: boolean;
  is_staff?: boolean;
  isSuperuser?: boolean;
  is_superuser?: boolean;
};

/**
 * Misma idea que backend `user_is_abs_admin`.
 * Gate de sesión: `isSuperuser` / `is_superuser` **o** `isAdmin` / `is_admin`
 * (login y GET `/me/`; camelCase del renderer). Fase 1 no manda `is_staff`.
 * `isStaff` leftover se acepta si llegara. El nombre del rol no otorga.
 */
export function isAbsAdminUser(user: NotasUser | undefined | null): boolean {
  if (!user) return false;
  if (user.isSuperuser === true || user.is_superuser === true) return true;
  if (user.isAdmin === true || user.is_admin === true) return true;
  if (user.isStaff === true || user.is_staff === true) return true;
  return false;
}

type NotaFechaSource = {
  created_at?: string | null;
  createdAt?: string | null;
  created_at_display?: string | null;
  createdAtDisplay?: string | null;
};

/** ISO del renderer camelCase (`createdAt`) o snake_case. */
export function notaCreatedAtIso(nota: NotaFechaSource): string {
  return (nota.created_at || nota.createdAt || "").trim();
}

/**
 * Parsea ISO de nota. Microsegundos → ms; naive se trata como America/Argentina/Buenos_Aires.
 */
export function parseNotaDate(iso: string | null | undefined): Date | null {
  if (!iso || typeof iso !== "string") return null;
  let s = iso.trim();
  if (!s) return null;
  s = s.replace(/\.(\d{3})\d+/, ".$1");
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s}-03:00`;
  }
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Título de fila: fecha y hora es-AR (`13/08/2026 17:32`). */
export function formatNotaFecha(iso: string | null | undefined): string {
  if (!iso || typeof iso !== "string" || !iso.trim()) return "";
  const date = parseNotaDate(iso);
  if (!date) return iso.trim();
  const formatted = new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  return formatted.replace(",", "").replace(/\s+/g, " ").trim();
}

/** Prefiere `createdAtDisplay` del API; si no, formatea ISO (`createdAt` / `created_at`). */
export function notaFechaLabel(nota: NotaFechaSource): string {
  const display = (nota.created_at_display || nota.createdAtDisplay || "").trim();
  if (display) return display;
  return formatNotaFecha(notaCreatedAtIso(nota));
}

const TITULO_VACIO = "(Sin título)";

/** Título de fila; notas viejas sin `titulo` → “(Sin título)”. */
export function notaTituloLabel(nota: { titulo?: string | null }): string {
  const titulo = (nota.titulo || "").trim();
  return titulo || TITULO_VACIO;
}
